import { useState } from 'react';
import type { AiAttachableDocument } from '@accounting/shared';
import { useAttachableDocuments } from '../../hooks/useAiChat';

interface Props {
  open: boolean;
  selectedIds: string[];
  onToggle: (doc: AiAttachableDocument) => void;
  onClose: () => void;
}

/**
 * Pick documents from the vault to put in front of the assistant.
 *
 * Only filing metadata crosses the wire here — the file bytes stay server-side
 * and the model is given the extracted text, so a 30MB PDF costs the browser
 * nothing.
 */
export function AttachDocumentPicker({ open, selectedIds, onToggle, onClose }: Props) {
  const [q, setQ] = useState('');
  const docs = useAttachableDocuments(q, open);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label="Attach a document"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(640px, 92vw)',
          maxHeight: '78vh',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          padding: 16,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: 16, margin: 0 }}>Attach from document vault</h2>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Done
          </button>
        </div>

        <input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by file name, vendor or summary…"
        />

        <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
          {docs.isLoading && <p className="muted">Loading…</p>}
          {!docs.isLoading && (docs.data?.length ?? 0) === 0 && (
            <p className="muted" style={{ fontSize: 13 }}>
              No documents found. Upload files to the document vault first — then you can ask
              questions about them here.
            </p>
          )}

          {(docs.data ?? []).map((d) => {
            const selected = selectedIds.includes(d.id);
            return (
              <button
                key={d.id}
                type="button"
                onClick={() => onToggle(d)}
                style={{
                  all: 'unset',
                  cursor: 'pointer',
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: selected ? 'var(--bg-surface-2)' : 'transparent',
                  border: `1px solid ${selected ? 'var(--accent)' : 'transparent'}`,
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontWeight: 500, fontSize: 13 }}>
                    {selected ? '✓ ' : ''}
                    {d.fileName}
                  </span>
                  <span className="muted" style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                    {d.documentDate ?? 'undated'}
                  </span>
                </div>
                <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                  {[d.companyName, d.docType, d.vendorName, d.summary]
                    .filter(Boolean)
                    .join(' · ')}
                  {d.hasText ? '' : ' · no text extracted'}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
